import React, { useState, useEffect, useCallback } from 'react';
import Header from './components/Header';
import Dashboard from './components/Dashboard';
import ActivityList from './components/ActivityList';
import ActivityModal from './components/ActivityModal';
import FilterBar from './components/FilterBar';
import Calendar from './components/Calendar';
import Login from './components/Login';
import Register from './components/Register';
import UserManagement from './components/UserManagement';
import ProgressView from './components/ProgressView';
import UserProfile from './components/UserProfile';
import { api, getToken, setToken } from './api';
import './App.css';

export default function App() {
  console.log('App component mounting...');
  const [currentUser, setCurrentUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [authView, setAuthView] = useState('login'); // 'login' | 'register'
  const [activities, setActivities] = useState([]);

  const [filter, setFilter] = useState({ status: 'all', category: 'all', priority: 'all' });
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);
  const [view, setView] = useState('list'); // 'list' | 'board' | 'calendar'
  const [showAddUser, setShowAddUser] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [mobileScreen, setMobileScreen] = useState('overview'); // 'overview' | 'chores' | 'users' for mobile
  const [desktopHome, setDesktopHome] = useState(true); // Desktop starts with dashboard homepage

  // Restore session from token on mount
  useEffect(() => {
    const token = getToken();
    if (!token) { setAuthChecked(true); return; }
    api.me()
      .then(({ user }) => setCurrentUser(user))
      .catch(() => setToken(null))
      .finally(() => setAuthChecked(true));
  }, []);

  const loadChores = useCallback(async () => {
    if (!currentUser) return;
    try {
      console.log('Loading chores for user:', currentUser);
      const { chores } = await api.listChores();
      console.log('Loaded chores:', chores);
      setActivities(chores || []);
    } catch (err) {
      console.error('Failed to load chores', err);
      console.error('Error details:', err.response?.data || err.message);
      // Set empty array to prevent blank page
      setActivities([]);
      if (err.status === 401) {
        setToken(null);
        setCurrentUser(null);
      }
    }
  }, [currentUser]);

  useEffect(() => { loadChores(); }, [loadChores]);

  const addActivity = async (data) => {
    const { chore } = await api.createChore(data);
    setActivities(prev => [chore, ...prev]);
  };

  const updateActivity = async (id, data) => {
    const { chore } = await api.updateChore(id, data);
    setActivities(prev => prev.map(a => a.id === id ? chore : a));
  };

  // Deactivate / reactivate a chore (toggles `active` flag).
  // Inactive chores stay in the list with a distinct visual state.
  const toggleActive = async (id) => {
    const { chore } = await api.toggleActive(id);
    setActivities(prev => prev.map(a => a.id === id ? chore : a));
  };

  const toggleStatus = async (id) => {
    const { chore } = await api.toggleChore(id);
    setActivities(prev => prev.map(a => a.id === id ? chore : a));
  };

  const openEdit = (activity) => {
    setEditingActivity(activity);
    setModalOpen(true);
  };

  // Handle calendar click - filter list by the chore's due date
  const handleCalendarClick = (activity) => {
    // Filter to show chores on the same date as clicked chore
    setFilter(prev => ({
      ...prev,
      search: '',
      status: 'all',
      priority: 'all',
      category: 'all',
      dueDate: activity.dueDate
    }));
    setView('list');
    setMobileScreen('chores');
  };

  const handleModalSave = async (data) => {
    try {
      if (editingActivity) {
        await updateActivity(editingActivity.id, data);
      } else {
        await addActivity(data);
      }
      setModalOpen(false);
      setEditingActivity(null);
    } catch (err) {
      alert(err.message || 'Failed to save activity');
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditingActivity(null);
  };

  const handleLogin = (user) => setCurrentUser(user);
  const handleRegister = (user) => setCurrentUser(user);
  const handleLogout = () => {
    setToken(null);
    setCurrentUser(null);
    setActivities([]);
  };

  const handleAddUser = () => {
    setShowAddUser(true);
  };

  const handleAddUserComplete = (newUser) => {
    setShowAddUser(false);
    // Optionally refresh user list or show success message
  };

  // Backend already filters by permissions, so use activities directly
  const accessibleActivities = activities;

  const filtered = accessibleActivities.filter(a => {
    if (filter.status !== 'all' && a.status !== filter.status) return false;
    if (filter.category !== 'all' && a.category !== filter.category) return false;
    if (filter.priority !== 'all' && a.priority !== filter.priority) return false;
    if (filter.dueDate && a.dueDate !== filter.dueDate) return false;
    if (search && !a.title.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const stats = {
    total: accessibleActivities.length,
    completed: accessibleActivities.filter(a => a.status === 'completed').length,
    pending: accessibleActivities.filter(a => a.status === 'pending').length,
    highPriority: accessibleActivities.filter(a => a.priority === 'high' && a.status === 'pending').length,
    dueToday: accessibleActivities.filter(a => {
      if (!a.dueDate || a.status === 'completed') return false;
      return a.dueDate === new Date().toISOString().split('T')[0];
    }).length,
  };

  // Debug logging for stats
  console.log('Dashboard stats:', stats);
  console.log('Accessible activities:', accessibleActivities);

  if (!authChecked) {
    console.log('App still loading auth...');
    return <div className="app-loading">Loading...</div>;
  }

  // Show login/register if not authenticated
  if (!currentUser) {
    console.log('User not authenticated, showing auth view:', authView);
    return authView === 'login' ? (
      <Login onLogin={handleLogin} onSwitchToRegister={() => setAuthView('register')} />
    ) : (
      <Register onRegister={handleRegister} onSwitchToLogin={() => setAuthView('login')} />
    );
  }

  console.log('User authenticated, rendering app for:', currentUser);

  // Ensure activities is always an array to prevent crashes
  const safeActivities = Array.isArray(activities) ? activities : [];
  const safeAccessibleActivities = Array.isArray(accessibleActivities) ? accessibleActivities : [];
  const safeFiltered = Array.isArray(filtered) ? filtered : [];
  
  console.log('Safe activities count:', safeActivities.length);
  console.log('Safe accessible activities count:', safeAccessibleActivities.length);

  try {
    return (
      <div className="app">
        <Header
          onAdd={() => setModalOpen(true)}
          search={search}
          onSearch={setSearch}
          view={view}
          onViewChange={setView}
          currentUser={currentUser}
          onLogout={handleLogout}
          onShowProgress={() => setShowProgress(true)}
          onShowProfile={() => setShowProfile(true)}
          mobileScreen={mobileScreen}
          onMobileScreenChange={setMobileScreen}
        />

        <main className="main">
          {/* Mobile View */}
          <div className="mobile-view">
            {mobileScreen === 'overview' && (
              <ProgressView
                currentUser={currentUser}
                onClose={() => setMobileScreen('chores')}
                onFilterChores={(newFilter) => {
                  // Handle dueToday filter specially
                  if (newFilter.dueToday) {
                    const today = new Date().toISOString().split('T')[0];
                    setFilter(prev => ({ ...prev, status: 'pending', dueDate: today }));
                  } else {
                    setFilter(prev => ({ ...prev, ...newFilter, dueDate: undefined }));
                  }
                  setView('list');
                  setMobileScreen('chores');
                }}
              />
            )}
            
            {mobileScreen === 'chores' && (
              <>
                {view === 'calendar' ? (
                  <Calendar
                    activities={safeAccessibleActivities}
                    onEventClick={handleCalendarClick}
                    onClose={() => setView('list')}
                  />
                ) : (
                  <>
                    <FilterBar 
                      filter={filter} 
                      onFilterChange={setFilter}
                      onGoHome={() => setMobileScreen('overview')}
                      showHomeButton={true}
                    />
                    <ActivityList
                      activities={safeFiltered}
                      view={view}
                      onToggle={toggleStatus}
                      onEdit={openEdit}
                      onToggleActive={toggleActive}
                      currentUser={currentUser}
                    />
                  </>
                )}
              </>
            )}
            
            {mobileScreen === 'users' && (
              <UserManagement 
                currentUser={currentUser} 
                showAddUser={showAddUser}
                onAddUserComplete={handleAddUserComplete}
              />
            )}
          </div>

          {/* Desktop View */}
          <div className="desktop-view">
            {desktopHome ? (
              <ProgressView
                currentUser={currentUser}
                onClose={() => setDesktopHome(false)}
                onFilterChores={(newFilter) => {
                  if (newFilter.dueToday) {
                    const today = new Date().toISOString().split('T')[0];
                    setFilter(prev => ({ ...prev, status: 'pending', dueDate: today }));
                  } else {
                    setFilter(prev => ({ ...prev, ...newFilter, dueDate: undefined }));
                  }
                  setView('list');
                  setDesktopHome(false);
                }}
              />
            ) : (
              <>
                <Dashboard 
                  stats={stats} 
                  onCreate={() => { setEditingActivity(null); setModalOpen(true); }}
                  currentUser={currentUser}
                  onAddUser={handleAddUser}
                />
                
                <UserManagement 
                  currentUser={currentUser} 
                  showAddUser={showAddUser}
                  onAddUserComplete={handleAddUserComplete}
                />

                {view === 'calendar' ? (
                  <Calendar
                    activities={safeAccessibleActivities}
                    onEventClick={handleCalendarClick}
                    onClose={() => setView('list')}
                  />
                ) : (
                  <>
                    <FilterBar 
                      filter={filter} 
                      onFilterChange={setFilter}
                      onGoHome={() => setDesktopHome(true)}
                      showHomeButton={true}
                    />

                    <ActivityList
                      activities={safeFiltered}
                      view={view}
                      onToggle={toggleStatus}
                      onEdit={openEdit}
                      onToggleActive={toggleActive}
                      currentUser={currentUser}
                    />
                  </>
                )}
              </>
            )}
          </div>
        </main>

      {modalOpen && (
        <ActivityModal
          activity={editingActivity}
          onSave={handleModalSave}
          onClose={handleModalClose}
          currentUser={currentUser}
        />
      )}

      {showProgress && (
        <ProgressView
          currentUser={currentUser}
          onClose={() => setShowProgress(false)}
          onFilterChores={(newFilter) => {
            // Handle dueToday filter specially
            if (newFilter.dueToday) {
              const today = new Date().toISOString().split('T')[0];
              setFilter(prev => ({ ...prev, status: 'pending', dueDate: today }));
            } else {
              setFilter(prev => ({ ...prev, ...newFilter, dueDate: undefined }));
            }
            setView('list');
            setShowProgress(false);
          }}
        />
      )}

      {showProfile && (
        <UserProfile
          currentUser={currentUser}
          onClose={() => setShowProfile(false)}
          onUpdateUser={(updatedUser) => setCurrentUser(prev => ({ ...prev, ...updatedUser }))}
        />
      )}
    </div>
  );
  } catch (error) {
    console.error('App rendering error:', error);
    console.error('Error stack:', error.stack);
    return (
      <div className="app-error">
        <h2>Application Error</h2>
        <p>There was an error loading the application. Please refresh the page.</p>
        <p><strong>Error:</strong> {error.message}</p>
        <button onClick={() => window.location.reload()}>Refresh</button>
      </div>
    );
  }
}
